/**
 * API response DTOs — the single source of truth for the web ↔ api contract.
 * Money is always integer minor units at 1/10,000 — four decimal places, NOT
 * cents (see ./money.ts, MONEY_SCALE). The client formats for display.
 */
import type { Role } from "./rbac";
import type { UserAccess } from "./access";
import type { DescriptionLang } from "./description";

export interface UserSecurityEventDto {
  id: string; userId: string; actorId: string | null; actorName: string | null; action: string; detail: string | null; createdAt: number;
}

export interface AuthUserDto {
  id: string;
  email: string;
  role: Role;
  displayName: string;
  mustChangePassword?: boolean;
  portalSupplierId?: string | null;
  /** Bảng quyền đã giải sẵn — sidebar và nút bấm đọc chính bảng máy chủ dùng. */
  access?: UserAccess;
}

export interface Paginated<T> {
  data: T[];
  page: number;
  pageSize: number;
  total: number;
}

/** Xưởng của khách đặt ở đâu — khách chốt 15/09/2026. Trước đó là "overseas" | "local". */
export type CustomerClass = "vietnam" | "china";

/** Một số tiền gắn đúng tiền tệ của nó — dùng khi một chỗ phải hiện nhiều tiền tệ cạnh nhau mà không cộng lại. */
export interface CurrencyAmountDto {
  currency: string;
  amount: number;
}

export interface CustomerDto {
  id: string;
  companyName: string;
  /** Mã khách / tên viết tắt (yêu cầu v1.8 mục 2.2). */
  code: string | null;
  phone: string | null;
  address: string | null;
  paymentTerm: string | null;
  classification: CustomerClass;
  /**
   * Người theo đơn (跟单员) — nhiều người (Roary 29/09/2026: "sometimes some guests have two order trackers working together").
   * Chính là `user_customers`: tài khoản "chỉ khách mình theo đơn" thấy khách này khi và chỉ khi có tên ở đây. Xếp theo lúc được
   * thêm; người đầu tiên được chép sang cột cũ `customer_service_user_id` cho những chỗ còn đọc một người.
   */
  followers: CustomerFollowerDto[];
  /** Tối đa 3 người liên hệ đầu, "Tên · điện thoại" — để gọi ngay từ danh sách. */
  contactPreview: string | null;
  contactsCount: number;
  /**
   * Công nợ mở theo TỪNG tiền tệ của PI (yêu cầu v1.8 mục 2.4, 11/10/2026) — không bao giờ cộng hai tiền tệ. Rỗng = khách chưa có
   * PI nào. Số theo đơn vị nhỏ nhất (`MONEY_SCALE`), âm khi thu dư.
   */
  openArByCurrency: CurrencyAmountDto[];
  createdAt: number;
  createdByName: string | null;
  /** P-Process default for new PIs raised for this customer. */
  pProcessDefault: boolean;
  /** Khách riêng tư (30/09/2026): chỉ quản trị viên thấy — người khác không bao giờ nhận được khách này. */
  isPrivate: boolean;
}

export interface CustomerFollowerDto {
  id: string;
  name: string;
  /** Tài khoản đã khoá: vẫn hiện để người sửa biết mà bỏ ra, nhưng không chọn thêm được. */
  active: boolean;
}

export interface CustomerContactDto {
  id: string;
  name: string;
  phone1: string | null;
  phone2: string | null;
  email: string | null;
  wechat: string | null;
  section: string | null;
  brandName: string | null;
  team: string | null;
  sortOrder: number;
  /** Liên hệ riêng tư (30/09/2026): chỉ quản trị viên thấy — người khác không nhận được dòng này. */
  isPrivate: boolean;
}

/** GET /customers/:id — the list endpoint keeps returning CustomerDto. */
export interface CustomerDetailDto extends CustomerDto {
  contacts: CustomerContactDto[];
}

export type PiPaymentStatus = "unpaid" | "partial" | "paid";

export interface PiSummaryDto {
  id: string;
  piNo: string;
  date: number | null;
  billToName: string;
  /** Công ty (khu vực dữ liệu) của khách — để danh sách "Tất cả công ty" ghi rõ mỗi PI thuộc công ty nào (v1.8 mục 1.1). */
  billToRegion: CustomerClass | null;
  /** Raw FK ids — the resolved *Name fields cannot preselect a <select>. */
  billToId: string | null;
  notifyPartyId: string | null;
  consigneeId: string | null;
  /** Primary contact person at the bill-to party. */
  billToContact: string | null;
  notifyPartyName: string | null;
  notifyPartyContact: string | null;
  consigneeName: string | null;
  consigneeContact: string | null;
  customerPoNo: string | null;
  currency: string;
  totalAmount: number;
  paid: number;
  paymentStatus: PiPaymentStatus;
  respBy: string | null;
  hasInvoice: boolean;
  /** SỐ hoá đơn đã xuất, để tra cứu theo số thay vì chỉ biết "đã/chưa xuất". */
  invNo: string | null;
  /** Number of line items on the PI. */
  itemCount: number;
  /** Our legal entity that collects payment for this PI. */
  paymentCompany: string | null;
  createdAt: number;
  createdByName: string | null;
  /** P-Process: this order's lines carry lot numbers. */
  pProcess: boolean;
  /** Chứng từ in bằng tiếng gì — quyết định lấy bản mô tả nào của mỗi dòng. */
  printLanguage: DescriptionLang;
  /** Chỉ quản trị viên thấy chứng từ này và mọi thứ phát sinh từ nó. */
  adminOnly: boolean;
  /** Red PI (mục 9): thuộc sổ Red, số dãy R. */
  isRed: boolean;
}

export interface PiItemDto {
  /** pi_items.id — packing lists match `pl_items.original_item_id` against it. */
  id: string;
  lineNo: number;
  productId: string | null;
  productCode: string | null;
  customerStyleNo: string | null;
  subRef: string | null;
  description: string | null;
  descriptionZh: string | null;
  color: string | null;
  size: string | null;
  quantity: number;
  unit: string | null;
  moq: number | null;
  mcq: number | null;
  msq: number | null;
  unitPrice: number;
  amount: number;
  dtdPrice: number | null;
  exwPrice: number | null;
  underMoqPrice: number | null;
  /** Supplier material batch, inherited down the document chain. */
  lotNumber: string | null;
  /**
   * Ảnh HIỆN TẠI của sản phẩm trên dòng (yêu cầu v1.8 mục 4.4) — đọc sống từ hồ sơ sản phẩm theo `productId`, không chép vào dòng:
   * sản phẩm thay ảnh thì dòng thấy ảnh mới. Dòng gõ tay không gắn sản phẩm = null ("No image").
   */
  imageUrl: string | null;
}

export interface PaymentBankDto {
  beneficiary: string;
  beneficiaryBank: string | null;
  bankAddress: string | null;
  accountNumberHkd: string | null;
  accountNumberUsd: string | null;
  /**
   * Tài khoản RMB. TUỲ CHỌN vì chứng từ lập trước 16/09/2026 mang ảnh chụp
   * ngân hàng chưa có trường này — vắng mặt nghĩa là không có, bản in bỏ dòng.
   */
  accountNumberRmb?: string | null;
  swiftCode: string | null;
  /**
   * Logo của chính pháp nhân thu tiền, để bản in dùng đúng logo đó.
   * Null thì bản in lùi về logo Hisense mặc định kèm theo ứng dụng.
   */
  logoUrl: string | null;
}

export interface PiPaymentDto {
  createdByName?:string|null;
  voidedByName?:string|null;
  createdBy?: string | null;
  voidedAt?: number | null;
  voidedBy?: string | null;
  voidReason?: string | null;
  id: string;
  date: number;
  amount: number;
  method: string | null;
  note: string | null;
}

export interface PiDetailDto extends PiSummaryDto {
  /**
   * Đơn bán hàng đang giữ PI này (chưa hủy), nếu có.
   *
   * PI bị KHOÁ sửa khi đơn đó đã xác nhận hoặc đã đóng, và một PI chỉ được
   * phép có một đơn chưa hủy (chỉ mục `so_one_pi`). Không hiện điều đó ngay
   * trên PI thì người dùng chỉ biết sau khi sửa xong và bị máy chủ từ chối —
   * hoặc tệ hơn, tưởng PI vẫn còn sửa được trong khi xưởng đã nhận lệnh.
   */
  salesOrder: { id: string; soNo: string; status: string } | null;
  priceSnapshots?: import('./documentPricing').LinePriceSnapshot[];
  updatedAt?:number;
  deliveryDate: number | null;
  shippingMode: string | null;
  paymentTerms: string | null;
  shipTo: string | null;
  customerDept: string | null;
  portOfLoading: string | null;
  countryOfOrigin: string | null;
  dnNo: string | null;
  shippingMarks: string | null;
  remarks: string | null;
  paymentCompanyId: string | null;
  /** Full multi-line consignee snapshot for print (list keeps the name line). */
  consigneeSnapshot: string | null;
  /** Banking details of the collecting entity — only on the detail payload. */
  paymentBank: PaymentBankDto | null;
  /** Minor units. */
  upcharge: number;
  /** Percent × 100. */
  discount: number;
  items: PiItemDto[];
  payments: PiPaymentDto[];
  /** P-Process, snapshotted from the customer when the PI was created. */
  pProcess: boolean;
  /** Chứng từ in bằng tiếng gì — quyết định lấy bản mô tả nào của mỗi dòng. */
  printLanguage: DescriptionLang;
}

export type ActivityKind =
  | "payment_recorded"
  | "pi_created"
  | "invoice_created"
  | "quotation_created"
  | "po_created";

export interface ActivityDto {
  id: string;
  ref: string;
  who: string;
  kind: ActivityKind;
  method: string | null;
  amount: number;
  currency: string;
  when: number;
  tone: "brand" | "ok" | "warn" | "info" | "brass" | "neutral";
  /**
   * Đường dẫn tới chính chứng từ đó.
   *
   * Trước đây dòng hoạt động không bấm được — người dùng thấy "PI 000378 vừa
   * được tạo" rồi phải tự sang danh sách gõ lại số để mở nó ra.
   */
  href: string | null;
}

export interface DashboardDto {
  /**
   * Đơn vị tiền của MỌI con số tiền trong DTO này.
   *
   * Trước đây dashboard cộng thẳng total_amount bất kể loại tiền rồi in ký hiệu
   * $. Hôm nay 267/267 PI đều USD nên chưa ai thấy sai, nhưng chứng từ HKD đầu
   * tiên sẽ làm doanh thu sai mà không có dấu hiệu nào. Giờ chỉ cộng chứng từ
   * cùng một loại tiền, và nói ra là loại nào.
   */
  currency: string;
  /**
   * Loại tiền bị BỎ QUA khỏi các con số trên, kèm số chứng từ. Rỗng là mọi thứ
   * đã được tính. Khác rỗng thì giao diện phải nói cho người xem biết — một con
   * số thiếu mà im lặng còn nguy hiểm hơn không có con số.
   */
  excludedCurrencies: { currency: string; count: number }[];
  revenueMTD: number;
  /** Doanh thu tháng trước, cùng đơn vị tiền — để tính phần trăm thay đổi thật. */
  revenuePrevMonth: number;
  revenueSpark: number[];
  openPis: number;
  arOutstanding: number;
  arOverdue: number;
  samplesInProgress: number;
  lowStock: number;
  /**
   * Số sản phẩm CHƯA ĐẶT ngưỡng đặt lại hàng.
   *
   * Không có con số này thì `lowStock: 0` trông như mọi thứ đều ổn, trong khi
   * sự thật có thể là chưa ai đặt ngưỡng cho sản phẩm nào.
   */
  lowStockWithoutThreshold: number;
  arAging: { bucket: string; amount: number }[];
  topCustomers: { name: string; amount: number }[];
  recentActivity: ActivityDto[];
  /**
   * Vài con số ĐẾM CÓ VIỆC ĐỂ LÀM, kèm đường dẫn bấm vào.
   *
   * Hệ cũ đếm "tổng số hoá đơn / tổng số PO / tổng số sản phẩm". Những con số
   * đó chỉ lớn dần theo thời gian và không nói ai phải làm gì. Ở đây đếm thứ
   * đang CHỜ XỬ LÝ: chứng từ chưa thu đủ tiền, đơn mua chưa nhận đủ hàng, báo
   * giá sắp hết hiệu lực.
   */
  worklist: { key: string; count: number; to: string }[];
  /** Số chứng từ lập theo từng ngày trong 7 ngày gần nhất (cũ → mới). */
  activity7d: { day: string; pis: number; invoices: number; purchaseOrders: number }[];
  /**
   * Việc kho tồn đọng (18/09/2026, Kho & LOT là nguồn tồn duy nhất). `null` = chưa bắt đầu quản lý tồn theo lô
   * (chưa ghi sổ phiếu tồn đầu kỳ nào) — giao diện chỉ đường sang nhập tồn đầu kỳ thay vì báo "không có việc".
   */
  inventoryExceptions: InventoryExceptionsDto | null;
}

export interface InventoryExceptionList {
  count: number;
  /** Tối đa 5 chứng từ mới nhất để bấm vào. */
  items: { id: string; no: string }[];
}
export interface InventoryExceptionsDto {
  /** Ngày ghi sổ tồn đầu kỳ đầu tiên (YYYY-MM-DD) — chỉ xét chứng từ từ ngày này. */
  since: string;
  /**
   * Phiếu giao đã rời kho (Đang giao / Đã giao) mà dòng có số gửi chưa có nguồn hàng (xuất phát trước quy tắc), hoặc dòng
   * xuất kho mà khách nhận nhiều hơn số đã xuất.
   */
  deliveredNotIssued: InventoryExceptionList;
  /** Hoá đơn có mã đang có tồn trong kho mà chưa có phiếu giao đã rời kho ghi nguồn hàng cho mã đó. */
  invoicesFromStock: InventoryExceptionList;
  /** Đơn mua báo đã giao (một phần / hết) mà số nhập kho chưa đủ. */
  receivedNotStocked: InventoryExceptionList;
  /** Giao thẳng trong lúc kho đang có hàng mã đó, người có quyền Duyệt chưa xem lại (0088). */
  directWithStock: InventoryExceptionList;
}

/* ───────────────────────────── Settings ───────────────────────────── */

/**
 * Một pháp nhân thu tiền của chính công ty — thông tin in trên MỌI Proforma
 * Invoice và Invoice. Chỉ vai trò có `settings:manage` đọc được các cột ngân
 * hàng; dropdown chọn công ty vẫn chỉ nhận id + tên.
 */
export interface CompanyDetailDto {
  revision: number;
  active: boolean;
  updatedAt: number;
  id: string;
  beneficiary: string;
  beneficiaryBank: string | null;
  bankAddress: string | null;
  accountNumberHkd: string | null;
  accountNumberUsd: string | null;
  accountNumberRmb: string | null;
  swiftCode: string | null;
  logoUrl: string | null;
  logoKey: string | null;
  isDefault: boolean;
}

export interface CompanyInfoSettingsDto {
  name: string;
  address: string;
  phone: string;
  email: string;
  taxId: string;
}

/**
 * Admin-managed option lists used by forms across the ERP. New lists can be
 * added without changing the app_settings table or the Settings page shell.
 */
export interface SettingsOptionListsDto {
  productTypes: string[];
  productUnits: string[];
  /** Đơn vị khối lượng (g/kg/oz/lb) — form sản phẩm trước đây đóng cứng "g". */
  weightUnits: string[];
  /** Supplier-contact specialization dropdown (Settings → Catalog). */
  supplierSpecializations: string[];
  /** Customer / PO payment-terms dropdown (Settings → Catalog). */
  paymentTerms: string[];
  [key: string]: string[];
}

export interface BusinessOperationsSettingsDto {
  optionLists: SettingsOptionListsDto;
  /** Tên tiếng Hoa của giá trị danh mục (`optionLabels.ts`). Luôn có mặt sau khi máy chủ đọc cài đặt. */
  optionLabelsZh: Record<string, Record<string, string>>;
  /** Tỷ giá quy đổi đơn giá (yêu cầu v1.8 mục 6.3) — chỉ các cặp đã đặt, theo thứ tự `FX_PAIRS`. */
  fxRates: import("./fx").FxRate[];
  [key: string]: unknown;
}

export interface SystemSettingsDto {
  defaultCurrency: string;
  /**
   * Đơn vị mặc định cho SẢN PHẨM MỚI.
   *
   * Hệ cũ có mục "giá trị mặc định" này; bản mới đóng cứng "gross", nên mỗi
   * sản phẩm bán theo cái đều phải sửa tay một lần.
   */
  defaultProductUnit: string;
  language: string;
  /** Tiền tệ mặc định theo công ty (yêu cầu v1.8 mục 6.2) cho PI và sản phẩm mới: Việt Nam USD, Trung Quốc RMB. */
  defaultCurrencyByRegion: { vietnam: string; china: string };
}

export interface LabelPrinterSettingsDto {
  presetKey: string;
  widthMm: number;
  heightMm: number;
  /** Xoay nội dung 90° khi in — để khớp quy ước chiều giấy của driver. */
  rotate: boolean;
}

export interface SettingsDto {
  revision?: number;
  /**
   * Ai sửa Cài đặt lần cuối và lúc nào.
   *
   * Đây là trang đổi được số tài khoản ngân hàng in trên mọi chứng từ; không
   * biết ai vừa đổi gì thì không có cách nào truy lại sau một sự cố.
   */
  updatedAt?: number | null;
  updatedByName?: string | null;
  companyInfo: CompanyInfoSettingsDto;
  businessOperations: BusinessOperationsSettingsDto;
  system: SystemSettingsDto;
  labelPrinter: LabelPrinterSettingsDto;
}

/** Derive payment status from amounts (shared by api compute + any client check). */
export function derivePaymentStatus(paid: number, total: number): PiPaymentStatus {
  if (paid <= 0) return "unpaid";
  if (paid >= total) return "paid";
  return "partial";
}

/* ───────────────────────────── Suppliers ───────────────────────────── */

export interface SupplierContactDto {
  id: string;
  name: string;
  phone1: string | null;
  phone2: string | null;
  email: string | null;
  wechat: string | null;
  specialization: string | null;
  sortOrder: number;
  /** Liên hệ riêng tư (0097) — chỉ quản trị viên nhận được dòng này, nên với người khác luôn là `false`. */
  isPrivate: boolean;
}

/** Người theo dõi nhà cung cấp (0097) — cùng hình dạng với người theo đơn của khách. */
export type SupplierFollowerDto = CustomerFollowerDto;

export interface SupplierDto {
  id: string;
  companyName: string;
  address: string | null;
  phone: string | null;
  email: string | null;
  contactsCount: number;
  /**
   * Mã đăng nhập cổng NCC, `null` = chưa mở cổng cho nhà cung cấp này.
   *
   * KHÔNG phải bí mật — nó là tên đăng nhập, mật khẩu mới chứng minh danh
   * tính. Nhân viên phải đọc được để còn báo cho nhà cung cấp.
   */
  portalLoginId: string | null;
  /**
   * Đội dùng nhà cung cấp này (Roary 29/09/2026: "Suppliers also need to be separated between Vietnam and China" — "We will
   * separate on both sides"). `null` = CHƯA PHÂN LOẠI: nhà cung cấp có từ trước ngày chia, cả hai đội thấy cho tới khi Hisense
   * chọn. Cùng một xưởng mà hai đội cùng mua thì mỗi đội giữ một hồ sơ riêng.
   */
  classification: CustomerClass | null;
  /**
   * Nhà cung cấp riêng tư (Roary 09/10/2026, anh Toàn chốt 28/10/2026): hồ sơ chỉ quản trị viên và người theo dõi thấy; chứng từ
   * vẫn hiện với người khác, tên là "Nhà cung cấp nội bộ".
   */
  isPrivate: boolean;
  /** Người theo dõi (0097), xếp theo lúc được thêm. Với nhà cung cấp riêng tư: chính là những người được thấy, ngoài quản trị viên. */
  followers: SupplierFollowerDto[];
  /** Ordered by sortOrder. List and detail share this so the table can name people. */
  contacts: SupplierContactDto[];
  createdAt: number;
  createdByName: string | null;
}

/** Chi tiết nhà cung cấp hiện trùng hình dạng với dòng danh sách — giữ tên riêng để chỗ gọi nói đúng ý định. */
export type SupplierDetailDto = SupplierDto;

/* ───────────────────────────── Products ────────────────────────────── */

export const PRODUCT_APPROVAL_STATUSES = [
  "Pending",
  "Approved",
  "Rejected",
  "Under Review",
] as const;
export type ApprovalStatus = (typeof PRODUCT_APPROVAL_STATUSES)[number];

export interface ProductSummaryDto {
  id: string;
  productCode: string;
  /** Bản tiếng Anh. */
  description: string | null;
  descriptionZh: string | null;
  descriptionPrimary: DescriptionLang;
  subRef: string | null;
  customerRef: string | null;
  type: string | null;
  brandName: string | null;
  color: string | null;
  /** Màu tiếng Hoa; `color` là bản tiếng Anh. */
  colorZh: string | null;
  size: string | null;
  unit: string | null;
  /** Wire dimensions, serialized JSON ({length, width, diameter, …}). */
  wireSize: string | null;
  /** Thông tin vật liệu (Roary 09/10/2026). */
  materialType: string | null;
  materialWidth: number | null;
  materialWidthUnit: string | null;
  materialThickness: number | null;
  materialThicknessUnit: string | null;
  /** Độ cứng / trạng thái tôi, chữ tự do ("H14", "1/2H", "HV 150"). */
  materialHardness: string | null;
  /** Grams per 100 units — fractional in the legacy data (e.g. 2.9). */
  weightPer100: number | null;
  weightUnit: string | null;
  /** Ngưỡng đặt lại hàng; null = chưa đặt, không cảnh báo tồn thấp. */
  reorderPoint: number | null;
  moq: number | null;
  mcq: number | null;
  msq: number | null;
  sellingPrice: number | null; // minor units
  lowestPrice: number | null;
  dtdPrice?: number | null;
  exwPrice?: number | null;
  underMoqPrice?: number | null;
  upCharge: number | null;
  currency: string;
  sampleLeadTime: string | null;
  bulkLeadTime: string | null;
  /**
   * Tồn DÙNG ĐƯỢC theo Kho & LOT, đúng đơn vị của sản phẩm (migration 0087, 18/09/2026) — không còn là
   * số gõ tay. Kho đã xoá và bán thành phẩm đang ở xưởng gia công không tính.
   */
  stockQuantity: number;
  /** Tồn thực có trong kho theo lô: dùng được + chờ kiểm / đang chuyển + không đạt. */
  stockOnHand: number;
  /** Số gõ tay chép từ hệ cũ (`products.stock_quantity`) — chỉ để đối chiếu khi kiểm kê đầu kỳ. */
  legacyStockQuantity: number;
  storage: string | null;
  location: string | null;
  /** Mã khuôn và nơi cất khuôn — không phải chỗ để hàng trong kho. */
  moldRef: string | null;
  moldLocation: string | null;
  approvalStatus: ApprovalStatus;
  /**
   * Ảnh chứng minh phê duyệt — CÓ trên danh sách để hiện dấu hiệu và mở xem.
   * Trạng thái "Approved" không nói được là đã có bằng chứng hay chưa.
   */
  approvalImageUrl: string | null;
  /**
   * Ghi chú của sản phẩm — CÓ trên danh sách để chọn từ danh mục thì điền sẵn
   * vào dòng chứng từ (hệ cũ làm vậy). Người lập vẫn sửa được.
   */
  remarks: string | null;
  imageUrl: string | null;
  /** The R2 key behind imageUrl — a picker that copies the image must copy both. */
  imageKey: string | null;
  createdAt: number;
  createdByName: string | null;
  /** Redacted to null for roles lacking supplier_cost:view. */
  costUnitPrice: number | null;
  /** The FK behind supplierName: same identity, so the same redaction gate.
   *  Lets a supplier-scoped picker (purchase orders) filter on the summary row. */
  supplierId: string | null;
  supplierName: string | null;
  supplierProductRef: string | null;
}

export interface ProductDetailDto extends ProductSummaryDto {
  dtdPrice: number | null;
  exwPrice: number | null;
  underMoqPrice: number | null;
  remarks: string | null;
  approvalImageKey: string | null;
  /** Tồn theo lô tách phần — đơn vị của sản phẩm; lô mang đơn vị khác liệt kê riêng, không cộng lẫn. */
  stockBreakdown: ProductStockBreakdown;
}

export interface ProductStockBreakdown {
  available: number;
  /** Chờ kiểm hàng nhập (IQC) hoặc đang chuyển kho. */
  pending: number;
  rejected: number;
  onHand: number;
  otherUnits: { unit: string; onHand: number; available: number }[];
}

/* ── Per-customer pricing + history (product_prices) ── */

export type PriceSource = "customer" | "list" | "none";

export interface ProductPriceDto {
  id: string;
  customerId: string | null; // null = list price
  customerName: string | null;
  price: number; // minor units
  currency: string;
  effectiveFrom: number;
  effectiveTo: number | null; // null = currently effective
}

/** Resolved current price for a (product, customer) pair. */
export interface CurrentPriceDto {
  price: number | null;
  currency: string;
  source: PriceSource;
}

/**
 * Lần mua gần nhất của MỘT khách cho MỘT sản phẩm (yêu cầu v1.8 mục 4.6, 11/10/2026) — mọi ô lấy từ cùng một dòng PI: ngày PI, số
 * PI đủ, đơn giá thật, tiền tệ của PI, đơn vị tính của dòng. Chỉ để tham khảo; không tự ghi đè giá trên PI đang lập.
 */
export interface LastSaleDto {
  productId: string;
  piId: string;
  piNo: string;
  date: number | null;
  unitPrice: number;
  currency: string;
  unit: string | null;
}

/** Tệp bằng chứng đặt hàng trên PI (yêu cầu v1.8 mục 4.3). `url` là đường của chính PI — có kiểm phạm vi. */
/** Ảnh / tệp trên Quy trình P (migration 0099). */
export interface PProcessAttachmentDto extends PiAttachmentDto {
  step: string;
}

/** GET /p-process/attachments?piId= — ảnh mọi ô của PI, cộng bằng chứng đặt hàng của PI (chỉ khi được xem PI). */
export interface PProcessAttachmentsDto {
  attachments: PProcessAttachmentDto[];
  /** Bằng chứng đặt hàng trên PI, hiện ở nấc "Khách đặt hàng". `null` khi người xem không có quyền xem PI. */
  orderEvidence: PiAttachmentDto[] | null;
}

export interface PiAttachmentDto {
  id: string;
  kind: "image" | "document";
  fileName: string;
  fileSize: number;
  mimeType: string;
  url: string;
  createdAt: number;
  createdByName: string | null;
}

/** Một mục yêu cầu sản phẩm / bao bì của khách (yêu cầu v1.8 mục 2.3). */
export interface CustomerRequirementDto {
  id: string;
  category: import("./schemas").CustomerRequirementCategory;
  title: string | null;
  description: string | null;
  photos: { id: string; fileName: string; fileSize: number; mimeType: string; url: string }[];
  updatedAt: number;
  updatedByName: string | null;
}

/* ── M3 — Sales documents ── */

export interface DocLineDto {
  lineNo: number;
  productCode: string | null;
  description: string | null;
  descriptionZh?: string | null;
  color: string | null;
  size: string | null;
  quantity: number;
  unit: string | null;
  unitPrice: number;
  amount: number;
  /** Supplier material batch, inherited down the document chain. */
  lotNumber: string | null;
}

export interface QuotationSummaryDto {
  /** Chứng từ in bằng tiếng gì — quyết định lấy bản mô tả nào của mỗi dòng. */
  printLanguage: DescriptionLang;
 status:"Draft"|"Issued";revision:number;validUntil:number|null;validityTerms:string|null;updatedAt:number;deletedAt?:number|null;workflowVersion?:number;

  id: string;
  quotationNo: string;
  customerId: string | null;
  customerName: string | null;
  /** Công ty của khách (v1.8 mục 1.1). */
  customerRegion?: CustomerClass | null;
  /** Bill-to company as written on the quotation (may differ from customerName). */
  billToName: string | null;
  issueDate: number | null;
  preparedBy: string | null;
  currency: string;
  totalAmount: number;
  /** Tối đa 3 mã hàng đầu tiên — xem nhanh báo giá cho mặt hàng nào. */
  itemPreview?: string | null;
  itemCount: number;
  remarks: string | null;
  createdAt: number;
  createdByName: string | null;
}
/** quotation_items columns that DocLineDto does not carry. */
export interface QuotationLineDto extends DocLineDto {
 id?:string;productId?:string|null;
  subRef: string | null;
  moq: number | null;
  mcq: number | null;
  msq: number | null;
  sampleLeadTime: string | null;
  bulkLeadTime: string | null;
  imageUrl: string | null;
  remark: string | null;
  currency: string;
  /**
   * Catalog selling prices joined via productId for the commercial print.
   * quotation_items itself only stores unitPrice (legacy Firebase items
   * carried these three as editable copies; we never migrated those columns).
   */
  dtdPrice: number | null;
  exwPrice: number | null;
  underMoqPrice: number | null;
}

/** Customer contact block on the quotation print ("To:" box). */
export interface QuotationPrintCustomerDto {
  companyName: string;
  address: string | null;
  phone: string | null;
  email: string | null;
}

export interface QuotationDetailDto extends QuotationSummaryDto {
  priceSnapshots?: import('./documentPricing').LinePriceSnapshot[];
 contactId?:string|null;
 events?:{id:string;revision:number;kind:string;actorName:string|null;createdAt:number}[];

  attn: string | null;
  printingDate: number | null;
  /** Raw quotations.bill_to value (billToName is the joined company name). */
  billTo: string | null;
  /** Null when the quotation has no customer (legacy "unknown"). */
  printCustomer: QuotationPrintCustomerDto | null;
  items: QuotationLineDto[];
}

export type PoStatus =
  | "Draft" | "Sent" | "Confirmed" | "Partially Delivered" | "Delivered" | "Cancelled";

export interface PurchaseOrderSummaryDto {
  /** Chứng từ in bằng tiếng gì — quyết định lấy bản mô tả nào của mỗi dòng. */
  printLanguage: DescriptionLang;
  sourcingCaseId?:string|null;
  id: string;
  poNumber: string;
  supplierId: string | null;
  supplierName: string | null;
  orderDate: number | null;
  status: PoStatus;
  /** Free-text urgency flag set by the buyer (e.g. "High"). */
  priority: string | null;
  expectedDeliveryDate: number | null;
  currency: string;
  total: number;
  linkedPiNo: string | null;
  itemCount: number;
  /**
   * Hai mã hàng đầu tiên, dạng "MÃ ×SL, MÃ ×SL" — xem nhanh PO mua cái gì.
   * CHỈ có ở danh sách; trang chi tiết đã hiện đủ dòng hàng nên không cần.
   */
  itemPreview?: string | null;
  createdAt: number;
  createdByName: string | null;
}
/** po_items shape — DocLineDto loses sub_ref, moq, remarks and supplier_name. */
export interface PoLineDto {
  id?:string;
  unit?:string|null;piItemId?:string|null;
  lineNo: number;
  productId: string | null;
  productCode: string | null;
  subRef: string | null;
  description: string | null;
  descriptionZh?: string | null;
  color: string | null;
  size: string | null;
  moq: number | null;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  remarks: string | null;
  supplierName: string | null;
  /** Supplier material batch, inherited down the document chain. */
  lotNumber: string | null;
}

export interface PurchaseOrderDetailDto extends PurchaseOrderSummaryDto {
  /**
   * Nhà cung cấp riêng tư người xem không được thấy (28/10/2026): máy chủ che tên / liên hệ và đặt cờ này (`maskHiddenSuppliers`);
   * đơn chỉ xem, sửa là 403 `private_supplier`.
   */
  supplierHidden?: boolean;
  priceSnapshots?: import('./documentPricing').LinePriceSnapshot[];
  sourcePProcess?:boolean;
  sourcingReference?:{rfqNo:string;prNo:string;quoteReference:string};
  paymentTerms: string | null;
  shippingMethod: string | null;
  actualDeliveryDate: number | null;
  linkedPiId: string | null;
  subtotal: number;
  shipping: number;
  /** Percent × 100. */
  taxPercentage: number;
  tax: number;
  /**
   * Ảnh chụp lúc phát hành; lùi về danh bạ với PO cũ hơn migration 0009.
   * Đơn đặt hàng là cam kết đã phát đi, không phải khung nhìn động vào danh bạ.
   */
  supplierAddress: string | null;
  supplierPhone: string | null;
  supplierEmail: string | null;
  /** Người liên hệ bên NCC, chốt lại trên chứng từ. */
  supplierContact: string | null;
  remarks: string | null;
  items: PoLineDto[];
}

export interface PackingListSummaryDto {
  piId?:string|null;
  customerName?:string|null;
  /** Công ty của khách trên PI nguồn (v1.8 mục 1.1 — danh sách "Tất cả công ty" ghi rõ công ty). */
  customerRegion?:CustomerClass|null;
  weightsComplete?:boolean;
  id: string;
  plNo: string;
  piNo: string | null;
  invoiceNo: string | null;
  date: number | null;
  shipTo: string | null;
  shippingMode: string | null;
  /** Staff member responsible for the shipment. */
  respBy: string | null;
  totalCtns: number;
  totalNw: number;
  totalGw: number;
  itemCount: number;
  createdAt: number;
  createdByName: string | null;
}
export interface PackingListItemDto {
  id?:string;
  lineNo: number;
  ctnNo: string | null;
  itemNo: string | null;
  subRef: string | null;
  customerPo: string | null;
  description: string | null;
  size: string | null;
  color: string | null;
  quantity: number;
  unit: string | null;
  nwKgs: number | null;
  gwKgs: number | null;
  measurementCm: string | null;
  /** pi_items.id this carton line was packed from. The update rebuilds every
   *  row, so the editor MUST send it back or the over-pack guard goes blind. */
  originalItemId: string | null;
  /** Supplier material batch, inherited down the document chain. */
  lotNumber: string | null;
}
export interface PackingListDetailDto extends PackingListSummaryDto {
  updatedAt?:number;
  pProcess?:boolean;
  cartonConflicts?:boolean;
  deliveryNotes?:{id:string;dnNo:string}[];
  shippingMarks: string | null;
  attention: string | null;
  piId: string | null;
  items: PackingListItemDto[];
}

export type DeliveryStatus = "Pending" | "In Transit" | "Delivered" | "Cancelled";

export interface DeliveryNoteSummaryDto {
 sourceId?:string|null;sourcePiId?:string|null;plNo?:string|null;deletedAt?:number|null;
 quantities?:{unit:string;planned:number;received:number}[];
  id: string;
  dnNo: string;
  refType: "packing" | "invoice" | null;
  billToName: string | null;
  /** Công ty của khách (v1.8 mục 1.1). */
  customerRegion?: CustomerClass | null;
  shipTo: string | null;
  /** Source documents this delivery note was raised from. */
  piNo: string | null;
  invoiceNo: string | null;
  date: number | null;
  deliveryDate: number | null;
  deliveryStatus: DeliveryStatus;
  /** Staff member responsible for the delivery. */
  respBy: string | null;
  totalItems: number;
  totalQuantity: number;
  createdAt: number;
  createdByName: string | null;
}
export interface DeliveryNoteItemDto {
 id?:string;originalItemId?:string|null;
  lineNo: number;
  ctnNo: string | null;
  itemNo: string | null;
  subRef: string | null;
  description: string | null;
  orderedQty: number;
  deliveredQty: number;
  unit: string | null;
  remarks: string | null;
  /** Supplier material batch, inherited down the document chain. */
  lotNumber: string | null;
  /** Cách giao, ghi lúc hàng rời kho (0088; bản 1.1.40 ghi lúc xác nhận "Đã giao"): xuất từ kho hay giao thẳng. Phiếu cũ: null. */
  fulfillment?: "Stock" | "Direct" | null;
  fulfillmentNote?: string | null;
  /** Giao thẳng: tồn xuất bán được của mã lúc chọn giao thẳng (> 0 = kho đang có hàng → người Duyệt xem lại). */
  directStockQuantity?: number | null;
  directReviewedAt?: number | null;
  /** Tên người đã xem lại. */
  directReviewedBy?: string | null;
  directReviewNote?: string | null;
}
export interface DeliveryNoteDetailDto extends DeliveryNoteSummaryDto {
 sourceAvailable?:boolean;sourcePiAvailable?:boolean;
 updatedAt?:number;pProcess?:boolean;receiverName?:string|null;workflowVersion?:number;
 events?:{id:string;kind:string;fromStatus:string|null;toStatus:string|null;reason:string|null;actorName:string|null;createdAt:number;changes?:{field:string;lineNo?:number;before:string|number|null;after:string|number|null}[]}[];
  plNo: string | null;
  /** Customer FK, so an edit form can bind it (billToName is only the label). */
  billTo: string | null;
  customerDept: string | null;
  deliveryMode: string | null;
  vehicleNo: string | null;
  driverName: string | null;
  driverPhone: string | null;
  remarks: string | null;
  items: DeliveryNoteItemDto[];
}

/* Invoice (1:1 from PI) — reuses the PI item shape. */
export interface InvoiceSummaryDto {
  /** Chứng từ in bằng tiếng gì — quyết định lấy bản mô tả nào của mỗi dòng. */
  printLanguage: DescriptionLang;
  deletedAt?:number|null;
  id: string;
  invNo: string;
  /** Source PI — the id, so an edit panel can preselect it. */
  piId: string;
  piNo: string | null;
  /** Invoice issue date (epoch ms) — distinct from createdAt. */
  date: number | null;
  customerPoNo: string | null;
  billToName: string | null;
  /** Công ty của khách (v1.8 mục 1.1). */
  billToRegion?: CustomerClass | null;
  /** Primary contact person at the bill-to party. */
  billToContact: string | null;
  notifyPartyName: string | null;
  notifyPartyContact: string | null;
  consigneeName: string | null;
  consigneeContact: string | null;
  currency: string;
  totalAmount: number;
  /** Number of line items on the invoice. */
  itemCount: number;
  /** Our legal entity that collects payment for this invoice. */
  paymentCompany: string | null;
  createdAt: number;
  createdByName: string | null;
}
export interface InvoiceDetailDto extends InvoiceSummaryDto {
  priceSnapshots?: import('./documentPricing').LinePriceSnapshot[];
  updatedAt?:number;
  pProcess?:boolean;
  deliveryNotes?:{id:string;dnNo:string}[];
  /** RAW invoices.ship_to — the full multi-line contact snapshot. Bind the edit
   *  textarea to THIS, never to billToContact (which keeps the name line only). */
  shipTo: string | null;
  remarks: string | null;
  respBy: string | null;
  upcharge: number;
  /** Percent × 100. */
  discount: number;
  customerDept: string | null;
  deliveryDate: number | null;
  shippingMode: string | null;
  portOfLoading: string | null;
  countryOfOrigin: string | null;
  dnNo: string | null;
  shippingMarks: string | null;
  paymentTerms: string | null;
  consigneeSnapshot: string | null;
  paymentBank: PaymentBankDto | null;
  items: (DocLineDto & { productId: string | null; subRef: string | null })[];
}

/* Statement — PI-based receivable reporting with explicit cutoffs and scope. */
export type StatementFilter = "all"|"outstanding"|"overdue"|"settled"|"credit"|"review";
export interface StatementLineDto {
 piId:string;piNo:string;date:number|null;deliveryDate:number|null;paymentTerms:string|null;
 dueAt:number|null;currency:string;total:number;paid:number;balance:number;paymentStatus:PiPaymentStatus;
 ageDays:number|null;overdueDays:number|null;bucket:string;
 openingBalance:number;periodBilled:number;periodPaid:number;periodAdjustments:number;receivable:number;credit:number;
 archived:boolean;piArchived?:boolean;invoiceId:string|null;invoiceNo:string|null;invoiceArchived:boolean;
 dueBasis:"piDelivery"|"unknown";dueReason:string|null;historyUncertain:boolean;
}
export interface StatementDto {
 customerId:string|null;customerName:string|null;
 /** Công ty của khách (v1.8 mục 1.1). */
 customerRegion:CustomerClass|null;
 currency:string;lines:StatementLineDto[];
 totalBilled:number;totalPaid:number;totalBalance:number;overdueAmount:number;overdueCount:number;
 openingBalance:number;periodBilled:number;periodPaid:number;periodAdjustments:number;receivable:number;credit:number;unagedAmount:number;historyLimited:boolean;
 buckets:{bucket:string;amount:number}[];
}
export interface StatementReportMeta {
 generatedAt:number;asOf:number;from:number|null;mode:"current"|"asOf";filter:StatementFilter;
 historyLimited:boolean;currentDocumentBasis:true;customerId?:string;currency?:string;q?:string;sort?:"date"|"-date"|"due"|"-balance";tzOffsetMinutes:number;source:"PI";
}
export interface StatementReportDto {data:StatementDto[];meta:StatementReportMeta;}

/* ── M4 — Samples ── */
export type SampleOrderType = "From Supplier" | "To Customer";
export type SampleStatus = "Draft" | "Sent" | "Received" | "Testing" | "Approved" | "Rejected" | "Completed";

export interface SampleItemDto {
  lineNo: number;
  productId: string | null;
  productNo: string | null;
  subRef: string | null;
  type: string | null;
  description: string | null;
  descriptionZh: string | null;
  brand: string | null;
  color: string | null;
  size: string | null;
  requestQuantity: number;
  retainedQuantity: number;
  remarks: string | null;
  /** Manual-entry lines have no productId and carry their own image. */
  isManualEntry: boolean;
  imageUrl: string | null;
  imageKey: string | null;
  controlSampleUrl: string | null;
  controlSampleKey: string | null;
  controlSampleDescription: string | null;
}

export interface SampleAttachmentDto {
  id: string;
  type: "image" | "document";
  fileName: string | null;
  fileUrl: string | null;
  fileKey: string | null;
  fileSize: number | null;
  mimeType: string | null;
  uploadedAt: number;
}
export interface SampleOrderSummaryDto {
  /** Nhà cung cấp riêng tư người xem không được thấy (28/10/2026) — tên, liên hệ bị che, đơn chỉ xem (403 `private_supplier`). */
  supplierHidden?: boolean;
  /** Công ty của khách nhận mẫu (v1.8 mục 1.1). */
  customerRegion?: CustomerClass | null;
  /** Thông tin gửi hàng mẫu. */
  courier: string | null;
  trackingNo: string | null;
  shippedAt: number | null;
  /** Chứng từ in bằng tiếng gì — quyết định lấy bản mô tả nào của mỗi dòng. */
  printLanguage: DescriptionLang;
  id: string;
  sampleOrderNumber: string;
  orderType: SampleOrderType;
  supplierName: string | null;
  customerName: string | null;
  orderDate: number | null;
  deliveryDate: number | null;
  status: SampleStatus;
  labTestRequest: string | null;
  /** True when at least one line has a control-sample image. Compute from
   * sample_order_items.control_sample_url IS NOT NULL — NOT from the legacy
   * control_sample json column, which holds the literal "1" on every row. */
  controlSample: boolean;
  itemCount: number;
  /** Number of files attached to the sample order. */
  attachmentCount: number;
  createdByName: string | null;
  /** Dòng đầu tiên — danh sách phải cho biết đơn này làm mẫu CÁI GÌ. */
  firstProductNo: string | null;
  firstProductDescription: string | null;
  firstProductSubRef?: string | null;
  /** Ảnh mẫu đối chứng đầu tiên, để hiện thumbnail bấm phóng to được. */
  controlSampleUrl: string | null;
}
export interface SampleOrderDetailDto extends SampleOrderSummaryDto {
  teamDepartment: string | null;
  remarks: string | null;
  /** Raw FK ids so an edit form can preselect the party. */
  supplierId: string | null;
  customerId: string | null;
  /** CHỐT trên đơn khi lập (0011); đơn cũ chưa có thì lùi về người liên hệ
   * đầu tiên của đối tác. Sửa được trên form. */
  contactPerson: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  items: SampleItemDto[];
  attachments: SampleAttachmentDto[];
}

export interface SampleCatalogPreviewDto {
  token: string;
  orderNumber: string;
  supplierName: string | null;
  lines: { lineNo: number; productNo: string; subRef: string | null; description: string | null;
    action: "create" | "link" | "alreadyLinked" | "blocked"; reason?: "incomplete" | "ambiguous" }[];
}

/** Kết quả đẩy dòng gõ tay của đơn mẫu lên danh mục sản phẩm. */
export interface SampleAddToCatalogResult {
  created: string[];
  skippedExisting: string[];
  skippedIncomplete: { productNo: string; missing: string }[];
}

export interface SampleCardDto {
  /** Công ty của khách (v1.8 mục 1.1). */
  customerRegion?: CustomerClass | null;
  id: string;
  sampleCardNumber: string;
  /** Catalog link — an edit panel cannot preserve it without the raw FK. */
  productId: string | null;
  productNo: string | null;
  /** Mã phụ — cột sub_ref đã có dữ liệu từ trước. */
  subRef: string | null;
  description: string | null;
  type: string | null;
  brand: string | null;
  color: string | null;
  size: string | null;
  imageUrl: string | null;
  imageKey: string | null;
  customerId: string | null;
  customerName: string | null;
  /** Customer's first contact — for the Sample Card print. Not stored on the card. */
  contactPerson: string | null;
  contactPhone: string | null;
  /** Email người liên hệ đã chốt trên phiếu (69/94 phiếu cũ có). */
  contactEmail: string | null;
  remarks: string | null;
  createdAt: number;
  createdByName: string | null;
}

/* ── M4 — Insight ── */
/** Kỳ báo cáo thực tế đã dùng, epoch ms — để giao diện hiện đúng cái nó đang xem. */
export interface InsightPeriodDto {
  from: number;
  to: number;
}

export interface AnalyticsDto {
  totalRevenue: number;
  /** Đơn vị tiền của MỌI con số trong DTO. Không cộng chéo tiền tệ. */
  currency: string;
  /** Loại tiền bị bỏ qua khỏi phép cộng, kèm số chứng từ. Rỗng = đã tính hết. */
  excludedCurrencies: { currency: string; count: number }[];
  period: InsightPeriodDto;
  /**
   * ĐẾM THẬT, không phải độ dài mảng top-8.
   *
   * Trước đây giao diện hiện `byProduct.length` và `byCustomer.length` làm KPI
   * "Sản phẩm đã bán" / "Khách hàng đang giao dịch" — mà hai mảng đó đã bị cắt
   * còn 8 phần tử, nên hai con số luôn tối đa bằng 8 dù thực tế là hàng trăm.
   */
  productCount: number;
  customerCount: number;
  orderCount: number;
  totalQuantity: number;
  /** Giá trị đơn trung bình = totalRevenue / orderCount. */
  averageOrderValue: number;
  byProduct: { code: string; qty: number; revenue: number }[];
  byCustomer: { name: string; revenue: number }[];
  byMonth: { month: string; revenue: number }[];
  /** Phân tích theo nhân viên phụ trách và theo nhóm hàng — hệ thống cũ có. */
  byRespBy: { name: string; revenue: number; orders: number }[];
  byProductType: { type: string; qty: number; revenue: number }[];
}
export interface ProfitLineDto {
  piNo: string;
  customerName: string | null;
  revenue: number;
  cost: number;
  margin: number;
  marginPct: number;
  /** Ngày phát hành PI — để đối chiếu dòng với kỳ báo cáo. */
  date: number | null;
  /** Số PO liên kết, cho biết giá vốn của dòng này lấy từ đâu. */
  poNos: string[];
  /** Số dòng hàng và tổng số lượng — kiểm tra nhanh dữ liệu có hợp lý không. */
  itemCount: number;
  quantity: number;
  /**
   * Có ít nhất một dòng hàng KHÔNG tra được giá vốn.
   *
   * Giá vốn thiếu bị tính là 0, nên dòng đó hiện lãi gần 100%. Hệ thống cũ để
   * im chuyện này; con số trông đẹp mà sai. Đánh dấu để người đọc biết dòng nào
   * không tin được, thay vì âm thầm thổi phồng lợi nhuận.
   */
  costIncomplete: boolean;
}
export interface ProfitSummaryDto {
  lines: ProfitLineDto[];
  totalRevenue: number;
  totalCost: number;
  totalMargin: number;
  currency: string;
  excludedCurrencies: { currency: string; count: number }[];
  period: InsightPeriodDto;
  /**
   * Số PI lấy được giá vốn từ đơn mua hàng liên kết, trên tổng số PI.
   *
   * Phần còn lại rơi về giá vốn niêm yết của sản phẩm — con số ước lượng, không
   * phải giá mua thật. Nói ra tỉ lệ này để người đọc biết báo cáo chắc tới đâu.
   */
  costFromPo: { withPo: number; total: number };
  /** Tổng số lượng đặt hàng trong kỳ — dòng TOTAL của bảng cũ có, KPI thì không. */
  totalQuantity: number;
  /** Số PI có dòng thiếu giá vốn, để cảnh báo ngay dưới thẻ KPI. */
  costIncompleteCount: number;
}

export interface DeletedRecordDto {
  id: string;
  originalTable: string;
  originalId: string;
  deletedAt: number;
  deletedByName: string | null;
  /** Snapshot fields lifted from the archived record, when it had them. */
  /** Số chứng từ thật (PI No / INV No / mã sản phẩm…), không phải UUID cắt ngắn. */
  documentNo: string | null;
  /** Tên đối tác trên chứng từ, để phân biệt hai bản ghi cùng loại. */
  partyName: string | null;
  customerPoNo: string | null;
  totalAmount: number | null;
  currency: string | null;
  /**
   * Bản ghi này khôi phục lại được hay không.
   *
   * Bảng xoá cứng vẫn được lưu trữ để có vết kiểm toán, nhưng KHÔNG dựng lại
   * được — hàng khôi phục chỉ gỡ cờ deleted_at, mà bảng đó không còn dòng nào.
   * Máy chủ nói ra thay vì để giao diện đoán, và nút Khôi phục không còn là một
   * lời hứa suông.
   */
  restorable: boolean;
}

export interface PoFromPiPreviewDto {
  piId: string;
  piNo: string;
  currency: string;
  token: string;
  groups: {
    supplierId: string | null;
    supplierName: string | null;
    existingPoNumber: string | null;
    items: {
      sourceLineId: string;
      productCode: string | null;
      subRef: string | null;
      lotNumber: string | null;
      description: string | null;
      quantity: number;
      unit?:string|null;
      unitPrice: number | null;
    }[];
  }[];
}

export interface PackingAvailabilityDto {piId:string;items:{id:string;quantity:number;allocated:number;remaining:number}[];}

export interface DeliverySourceDto {
 id:string;refType:"packing"|"invoice";number:string;piId:string|null;piNo:string|null;invoiceNo:string|null;plNo:string|null;
 /** Khóa ngoại hóa đơn của phiếu đóng gói nguồn, nếu nguồn là phiếu đóng gói. */
 invoiceId?:string|null;
 customerId:string|null;customerName:string|null;shipTo:string|null;pProcess:boolean;updatedAt:number;
 existing:{id:string;dnNo:string;status:DeliveryStatus;archived?:boolean;sourceId?:string|null}[];
 items:{id:string;ctnNo:string|null;itemNo:string|null;subRef:string|null;description:string|null;unit:string|null;lotNumber:string|null;quantity:number;remaining:number}[];
}

export type ProfitCostStatus="po"|"mixed"|"catalog"|"missing";
export interface ProfitQuantityDto {unit:string;quantity:number;}
export interface ProfitCostSourceDto {
 kind:"po"|"catalog";id:string;poItemId?:string;label:string;quantity:number;unitPrice:number;cost:number;currency:string;unit:string;updatedAt:number|null;capturedAt:number|null;legacyBaseline:boolean;poStatus?:string;
}
export interface ProfitItemCostDto {
 /** Doanh thu của riêng dòng. Tổng các dòng có thể KHÁC tổng PI khi chứng từ có tăng/giảm giá. */
 revenue:number;
 piItemId:string|null;productId:string|null;code:string|null;subRef:string|null;description:string|null;unit:string;quantity:number;cost:number|null;knownCost:number;poQuantity:number;catalogQuantity:number;missingQuantity:number;sources:ProfitCostSourceDto[];issues:string[];
}
export interface ProfitPoRefDto {id:string;poNo:string;status:string;currency:string;shipping:number;tax:number;}
export interface ProfitOrderDto {
 piId:string;piNo:string;customerId:string|null;customerName:string|null;date:number|null;currency:string;revenue:number;cost:number|null;margin:number|null;marginPct:number|null;knownCost:number;costStatus:ProfitCostStatus;poRefs:ProfitPoRefDto[];itemCount:number;
 /** PI này đã có hóa đơn chưa — cho bộ lọc "chỉ đã có hóa đơn" ở tab lãi theo mã hàng. */
 hasInvoice:boolean;quantityByUnit:ProfitQuantityDto[];issues:string[];items:ProfitItemCostDto[];
}
export interface ProfitCurrencyDto {
 currency:string;lines:ProfitOrderDto[];totalRevenue:number;pricedRevenue:number;totalCost:number;totalMargin:number;marginPct:number|null;knownCost:number;incompleteRevenue:number;costIncompleteCount:number;quantityByUnit:ProfitQuantityDto[];costCoverage:{withPo:number;total:number;catalog:number;mixed:number;missing:number};
}
export interface ProfitReportDto {
 groups:ProfitCurrencyDto[];period:{from:number;to:number};generatedAt:number;source:"PI";costBasis:"goods_only";tzOffsetMinutes:number;filters:{customerId?:string;currency?:string;q?:string;costStatus?:ProfitCostStatus|"all";profitState?:"all"|"profit"|"loss"|"breakeven"|"unknown";sort?:"-margin"|"margin"|"date"|"-date"|"-revenue"|"cost"|"-marginPct"};
}

export interface UserManagementDto {
 id:string; email:string; displayName:string; role:Role; status:'active'|'disabled';
 lastLoginAt:number|null; createdAt:number; updatedAt:number; createdBy:string|null; updatedBy:string|null; mustChangePassword:boolean;
 /** Khu vực dữ liệu (29/09/2026) — hiện trên danh sách để admin thấy ngay tài khoản nào chỉ một khu vực. */
 dataRegion:import('./access').DataRegion;
}

export interface ConfigurationEventDto {id:string;resource:string;recordId:string;action:string;fields:string|null;actorId:string|null;actorName:string|null;createdAt:number;}

export interface ReleaseSummary { title:string; changes:string[]; }
/** Tiếng Hoa: bản phát hành từ 21/09/2026 (giao diện đổi sang giản thể) ghi 'zh-Hans';
 * bản cũ hơn đã lưu trong app_releases chỉ có 'zh-Hant' và không sửa lại lịch sử. */
export type ReleaseNotes = Record<'en'|'vi',ReleaseSummary> & Partial<Record<'zh-Hans'|'zh-Hant',ReleaseSummary>>;
export interface ReleaseDto {id:string;version:string;deployedAt:number;notes:ReleaseNotes;source:'pipeline'|'verified_import';}
export interface ReleasesDto extends Paginated<ReleaseDto> {
 current:ReleaseDto|null; latest:ReleaseDto|null; loaded:ReleaseDto|null;
}

/**
 * Tài khoản cổng của MỘT nhà cung cấp — quản lý ngay trên hồ sơ nhà cung cấp.
 *
 * Không có mật khẩu ở đây, và không bao giờ có: máy chủ chỉ giữ hash. Cái màn
 * hình cần biết là "đã mở chưa, còn mật khẩu ban đầu không, đăng nhập lần cuối
 * khi nào" — đủ để admin biết nhà cung cấp đã vào được hay chưa.
 */
export interface SupplierPortalAccountDto {
  userId: string;
  /** Khoá duy nhất nội bộ (`<mã>@ncc.invalid`); nhà cung cấp đăng nhập bằng MÃ. */
  email: string;
  status: "active" | "disabled";
  /** Còn mật khẩu ban đầu do admin đặt; nhà cung cấp chưa tự đổi. */
  mustChangePassword: boolean;
  lastLoginAt: number | null;
  createdAt: number | null;
}

export interface SupplierPortalDto {
  loginId: string | null;
  account: SupplierPortalAccountDto | null;
}
